import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, ButtonBase, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';

import type { SessionLapOption } from '../../features/sessions/sessionContent';
import type { SessionListItem } from '../../features/sessions/sessionList';

export type ComparisonLapRailItem = {
  id: string;
  roleLabel: string;
  color: string;
  session: SessionListItem | null;
  selectedLapNumber: number | null;
  laps: SessionLapOption[];
  active: boolean;
  loading?: boolean;
  onLapChange?: (lapNumber: number) => void;
  onActivate?: () => void;
  onRemove?: () => void;
};

function comparisonSessionLabels(session: SessionListItem | null) {
  if (!session) {
    return { label: 'Loading session', detail: 'Session details unavailable' };
  }

  const time = session.recordedAtMs
    ? new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
        new Date(session.recordedAtMs),
      )
    : null;
  const detail = [time, session.trackName].filter(Boolean).join(' · ');
  return {
    label: session.driverName ?? 'Unassigned driver',
    detail: detail || session.title,
  };
}

export function SessionComparisonRail({
  items,
  isDark,
  onManage,
}: {
  items: ComparisonLapRailItem[];
  isDark: boolean;
  onManage?: () => void;
}) {
  return (
    <Box
      component="section"
      role="region"
      aria-label="Compared laps"
      data-testid="session-comparison-bar"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 1,
        px: { xs: 1, sm: 1.25 },
        py: 0.85,
        borderBottom: `1px solid ${isDark ? 'rgba(51,65,85,0.82)' : '#E5E7EB'}`,
        bgcolor: isDark ? '#07111B' : '#FFFFFF',
        flexShrink: 0,
        minWidth: 0,
      }}
    >
      <Stack spacing={0.05} sx={{ width: 104, flexShrink: 0, justifyContent: 'center', display: { xs: 'none', md: 'flex' } }}>
        <Typography sx={{ fontSize: '0.73rem', fontWeight: 900, color: isDark ? '#F8FAFC' : '#111827' }}>
          Compared laps
        </Typography>
        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: isDark ? '#94A3B8' : '#64748B' }}>
          {items.length} {items.length === 1 ? 'lap' : 'laps'} selected
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin', pb: 0.1 }}>
        {items.map((item) => {
          const selectedLap = item.laps.find((lap) => lap.lapNumber === item.selectedLapNumber);
          const sessionLabels = comparisonSessionLabels(item.session);
          return (
            <Box
              key={item.id}
              role="group"
              aria-label={item.roleLabel}
              aria-current={item.active ? 'true' : undefined}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(112px, 1fr) auto',
                alignItems: 'center',
                gap: 0.75,
                minWidth: { xs: 282, sm: 310 },
                maxWidth: 350,
                px: 0.9,
                py: 0.55,
                borderRadius: '8px',
                border: `1px solid ${item.active ? item.color : isDark ? 'rgba(51,65,85,0.9)' : '#E2E8F0'}`,
                borderLeft: `3px solid ${item.color}`,
                bgcolor: item.active
                  ? isDark ? 'rgba(30,41,59,0.92)' : '#FFFFFF'
                  : isDark ? 'rgba(15,23,42,0.72)' : '#F8FAFC',
                boxShadow: item.active ? `0 0 0 1px ${item.color}26` : 'none',
                cursor: item.onActivate ? 'pointer' : 'default',
                outline: 0,
                '&:focus-visible': {
                  boxShadow: `0 0 0 3px ${item.color}38`,
                },
              }}
            >
              <ButtonBase
                aria-label={`Show ${item.roleLabel} in GPS timeline`}
                aria-pressed={item.active}
                onClick={item.onActivate}
                disabled={!item.onActivate}
                sx={{ minWidth: 0, textAlign: 'left', justifyContent: 'flex-start', borderRadius: 1, '&:focus-visible': { outline: `2px solid ${item.color}`, outlineOffset: 2 } }}
              >
              <Stack spacing={0.08} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.55} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.62rem', lineHeight: 1.15, fontWeight: 900, color: item.color }}>
                    {item.roleLabel}
                  </Typography>
                  <Typography noWrap sx={{ minWidth: 0, fontSize: '0.7rem', lineHeight: 1.15, fontWeight: 850, color: isDark ? '#F8FAFC' : '#1F2937' }}>
                    {sessionLabels.label}
                  </Typography>
                </Stack>
                <Typography noWrap sx={{ fontSize: '0.61rem', lineHeight: 1.2, fontWeight: 650, color: isDark ? '#94A3B8' : '#64748B' }}>
                  {!item.onActivate ? item.loading ? 'Loading timeline…' : 'Timeline unavailable' : sessionLabels.detail}
                </Typography>
              </Stack>
              </ButtonBase>
              <Stack direction="row" spacing={0.35} sx={{ alignItems: 'center' }}>
                <Select
                  size="small"
                  value={item.selectedLapNumber ?? ''}
                  displayEmpty
                  disabled={item.loading || item.laps.length === 0 || !item.onLapChange}
                  inputProps={{ 'aria-label': `${item.roleLabel} lap` }}
                  renderValue={() => {
                    if (item.loading) return 'Loading…';
                    if (!selectedLap) return 'Choose lap';
                    return `${selectedLap.rowLabel} · ${selectedLap.label}`;
                  }}
                  onChange={(event) => item.onLapChange?.(Number(event.target.value))}
                  sx={{
                    minWidth: 112,
                    height: 30,
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    bgcolor: isDark ? 'rgba(2,10,18,0.72)' : '#FFFFFF',
                    '& .MuiSelect-select': { py: 0.6, pl: 0.9, pr: '24px !important' },
                    '& .MuiSelect-icon': { fontSize: 17 },
                  }}
                >
                  {item.laps.map((lap) => (
                    <MenuItem key={lap.lapNumber} value={lap.lapNumber} disabled={!lap.selectable} sx={{ fontSize: '0.75rem' }}>
                      {lap.rowLabel} · {lap.label}{lap.isBest ? ' · Best' : ''}
                    </MenuItem>
                  ))}
                </Select>
                {item.onRemove ? (
                  <Tooltip title="Remove from comparison">
                    <IconButton
                      size="small"
                      data-comparison-remove="true"
                      aria-label={`Remove ${item.roleLabel} from comparison`}
                      onClick={(event) => {
                        event.stopPropagation();
                        item.onRemove?.();
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        color: isDark ? '#94A3B8' : '#64748B',
                        '&:hover': {
                          color: isDark ? '#FCA5A5' : '#B91C1C',
                          bgcolor: isDark ? 'rgba(248,113,113,0.12)' : '#FEF2F2',
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {onManage ? (
        <Button
          variant="outlined"
          size="small"
          aria-label="Edit compared sessions"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={onManage}
          sx={{
            minWidth: { xs: 38, sm: 128 },
            px: { xs: 0.7, sm: 1 },
            alignSelf: 'center',
            height: 32,
            borderRadius: '7px',
            textTransform: 'none',
            fontSize: '0.7rem',
            fontWeight: 850,
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': { m: { xs: 0, sm: '0 5px 0 -2px' } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Edit comparisons
          </Box>
        </Button>
      ) : null}
    </Box>
  );
}
